import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import "./audio-wave.component.scss";

const AudioVisualizer = forwardRef(({ isStart, onStop }: any, ref) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const animationRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  useImperativeHandle(ref, () => ({
    stopRecording,
    stopMicTrack
  }));
  useEffect(() => {
    if (isStart) {
      startRecording();
    }
  }, [isStart]);
  useEffect(() => {
    const stopAll = () => {
      stopRecording();
      stopMicTrack();
    };
  
    window.addEventListener("beforeunload", stopAll);
    window.addEventListener("pagehide", stopAll);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) stopAll();
    });
  
    return () => {
      window.removeEventListener("beforeunload", stopAll);
      window.removeEventListener("pagehide", stopAll);
      document.removeEventListener("visibilitychange", stopAll);
    };
  }, []);
  useEffect(() => {
    const handleUnloadOrHide = () => {
      console.log("🔇 Stopping media stream before page unload or hide...");
      stopRecording(); // stop animation + recorder
      stopMicTrack();  // stop mic stream
    };
  
    // For tab close / reload
    window.addEventListener("beforeunload", handleUnloadOrHide);
    // For page navigation (back/forward, hidden tab, iOS app switch)
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) handleUnloadOrHide();
    });
    // For iOS specifically — triggers when app is backgrounded
    window.addEventListener("pagehide", handleUnloadOrHide);
  
    return () => {
      window.removeEventListener("beforeunload", handleUnloadOrHide);
      document.removeEventListener("visibilitychange", handleUnloadOrHide);
      window.removeEventListener("pagehide", handleUnloadOrHide);
    };
  }, []);

  async function getAudioStreamAndMimeType() {
    try {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      const possibleMimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg',
        'audio/mp4',
        'audio/aac'
      ];

      // Step 3: Select first supported MIME type
      const supportedMimeType = possibleMimeTypes.find(type => MediaRecorder.isTypeSupported(type));

      if (!supportedMimeType) {
        throw new Error('No supported MIME type found for MediaRecorder.');
      }

      return { supportedMimeType };
    } catch (err) {
      console.error('Error accessing microphone:', err);
      throw err; // Re-throw or handle as needed
    }
  }

  const startRecording = async () => {
    try {
      const { supportedMimeType } = await getAudioStreamAndMimeType();
      if (mediaStreamRef.current) {
        const recorder = new MediaRecorder(mediaStreamRef.current, { mimeType: supportedMimeType });
        const chunks: Blob[] = [];
        recorderRef.current = recorder;
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        recorder.onstop = async () => {
          const audioBlob = new Blob(chunks, { type: supportedMimeType });
          onStop(audioBlob);
        };
        const audioContext = new (window.AudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(mediaStreamRef.current);

        source.connect(analyser);
        analyser.fftSize = 256; // เพิ่มความละเอียดของแท่ง bar
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        dataArrayRef.current = dataArray;

        // setIsRecording(true);
        drawBars();
        recorder.start();
      }


    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert(error);
    }
  };

  const stopRecording = useCallback(() => {
    stopMicTrack();
  }, []);
  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) => {
    const r = Math.min(radius, height / 2);
    ctx.beginPath();
    ctx.moveTo(x, y + r);
    ctx.arcTo(x, y + height, x + width, y + height, r);
    ctx.arcTo(x + width, y + height, x + width, y, r);
    ctx.arcTo(x + width, y, x, y, r);
    ctx.arcTo(x, y, x, y + height, r);
    ctx.closePath();
    ctx.fill();
  };
  const drawBars = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasCtx = canvas.getContext("2d");
    if (!canvasCtx) return;

    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    if (!analyser || !dataArray) return;

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      // เคลียร์ Canvas ก่อนวาดใหม่
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;
      const barWidth = 6; // ความกว้างของแท่ง Bar
      const gap = 4; // ระยะห่างระหว่างแท่ง Bar
      const numBars = Math.floor(width / (barWidth + gap)) / 2; // แบ่งครึ่ง ซ้าย-ขวา

      // วาดเส้นฐาน
      canvasCtx.strokeStyle = "rgba(255, 255, 255, 0.3)";
      canvasCtx.lineWidth = 2;
      canvasCtx.beginPath();
      canvasCtx.moveTo(0, centerY);
      canvasCtx.lineTo(width, centerY);
      canvasCtx.stroke();

      // วาดแท่ง Bar ซ้าย-ขวา ออกจากกึ่งกลาง
      for (let i = 0; i < numBars; i++) {
        const amplitude = dataArray[i] / 255; // Normalize 0 - 1
        const barHeight = amplitude * (height * 0.4); // กำหนดความสูงของแท่ง Bar

        // คำนวณตำแหน่งแท่ง Bar (ซ้าย-ขวา)
        const xRight = centerX + i * (barWidth + gap);
        const xLeft = centerX - (i * (barWidth + gap) + barWidth);
        const yTop = centerY - barHeight;
        const yBottom = centerY + barHeight;

        // ใช้ Gradient Color
        const gradient = canvasCtx.createLinearGradient(0, yTop, 0, yBottom);
        gradient.addColorStop(0, "#4A25E1"); // สีบน
        gradient.addColorStop(1, "#FF7FD8"); // สีล่าง


        canvasCtx.fillStyle = gradient;

        drawRoundedRect(canvasCtx, xRight, yTop, barWidth, barHeight * 2, 5);
        drawRoundedRect(canvasCtx, xLeft, yTop, barWidth, barHeight * 2, 5);
      }
    };

    draw();
  };
  const stopMediaTracks = (stream: MediaStream | null) => {
    if (!stream) return;
    stream.getTracks().forEach((track) => track.stop());
  };
  const stopMicTrack = () => {
    stopMediaTracks(mediaStreamRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
  
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
};
  return (
    <div className="audio-wave-control">
      <canvas ref={canvasRef} className="audio-wave-canvas" ></canvas>
    </div>
  );
});

export default AudioVisualizer;
